import { Cancel, Close, CloudDone, CloudUpload, DescriptionOutlined, FileDownloadOutlined, UploadFile } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, Chip, Slide, CircularProgress, IconButton, Drawer, Grid, List, ListItem, Tab, Tabs, LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useState, useCallback } from 'react';
import PageContainer from '../../components/PageContainer';
import { submitCases } from '../../redux/api/cases';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { useDropzone } from 'react-dropzone';
import { INFO_COLOR, PRIMARY_COLOR, PRIMARY_COLOR_DARK, WARNING_COLOR } from '../../styles/colors';
import { getCasesUploadSummaryForCPD } from '../../redux/api/cases';
import Filters from '../../components/Filters';
import { getAllInstitutions } from '../../redux/api/institution';

export const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box>{children}</Box>}
        </div>
    );
};

const ReturnSubmission = () => {
    const [exporting, setExporting] = useState(false);
    const [records, setRecords] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [searching, setSearching] = useState(false);
    const [caseDetails, setCaseDetails] = useState({});
    const [openDrawer, setOpenDrawer] = useState(false);
    const [uploadProcess, setUploadProcess] = useState(''); // uploading, failed, success
    const [fileToUpload, setFileToUpload] = useState(null);
    const [paginationData, setPaginationData] = useState({});
    const [value, setValue] = React.useState(0);
    const [searchValue, setSearchValue] = useState('');

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const { enqueueSnackbar } = useSnackbar();

    const onDrop = useCallback((acceptedFiles) => {
        setFileToUpload(acceptedFiles[0]);
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: { 'text/xml': ['.xml'] },
    });

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.caseUploadHistoryId) || 0) + 1}</p>;
            },
        },
        {
            field: 'id',
            headerName: 'Action',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                            console.log(params?.row);
                            setCaseDetails(params?.row);
                            setOpenDrawer(true);
                        }}
                        title="More Details"
                    >
                        <DescriptionOutlined />
                    </IconButton>
                </div>
            ),
        },
        {
            field: 'caseUploadHistoryId',
            headerName: 'Set ID',
            width: 100,
            valueGetter: (params) => String(params?.row?.caseUploadHistoryId).padStart(4, '0'),
        },
        { field: 'originalFileName', headerName: 'Filename', width: 200, renderCell: (params) => params?.row?.originalFileName || params?.row?.fileName },
        {
            field: 'validation',
            headerName: 'Validation Status',
            width: 150,
            renderCell: (params) => getValidationStatus(params),
        },
        {
            field: 'processingStatus',
            headerName: 'Processing Status',
            width: 150,
            renderCell: (params) => getStatus(params?.row?.processingStatus),
        },
        {
            field: 'instCode',
            headerName: 'Institution',
            width: 220,
            renderCell: (params) => {
                const name = institutions?.find((i) => i.code == params?.row?.instCode)?.name || '';
                return name;
            },
        },

        { field: 'totalRecords', headerName: 'Total Records', width: 150 },
        { field: 'noOfFailedRecord', headerName: 'Failed Records', width: 150 },
        {
            field: 'noOfSuccessRecord',
            headerName: 'Success Records',
            width: 150,
        },
        {
            field: 'submissionDate',
            headerName: 'Submission Date',
            width: 200,
            valueGetter: (params) => moment(params?.row?.submissionTime).format('DD MMMM, YYYY'),
        },
        {
            field: 'submissionTime',
            headerName: 'Submission Time',
            width: 200,
            valueGetter: (params) => moment(params?.row?.submissionTime).format('hh:mm:ss A'),
        },
    ];

    // SUBMITTED, PROCESSING, PROCESSED
    const getStatus = (status = 'SUBMITTED') => {
        const label = status == 'PROCESSED' ? 'Processed' : status == 'PROCESSING' ? 'Processing' : 'Submitted';
        const color = status == 'PROCESSED' ? PRIMARY_COLOR_DARK : status == 'PROCESSING' ? WARNING_COLOR : INFO_COLOR;
        return <span style={{ color, fontWeight: 500 }}>{label}</span>;
    };

    const getValidationStatus = (params) => {
        const status = params?.row?.processingStatus == 'PROCESSED' ? (params?.row?.totalRecords == params?.row?.noOfSuccessRecord ? 'Success' : 'Failed') : 'Pending';
        return <Chip size="small" color={status == 'Pending' ? 'warning' : status == 'Failed' ? 'error' : 'success'} label={status} />;
    };

    const init = async (skip = 0, take = 50, instId = 0) => {
        try {
            setProcessing(true);
            const res = await getCasesUploadSummaryForCPD(skip, take, false, instId);
            setRecords(
                records?.length < skip
                    ? res?.resultObject?.caseUploadHistoryResponseVm?.caseUploadHistoryVms || []
                    : records.concat(res?.resultObject?.caseUploadHistoryResponseVm?.caseUploadHistoryVms || [])
            );
            setPaginationData({
                start: skip,
                length: take,
                total: res?.resultObject?.caseUploadHistoryResponseVm?.overallCount,
            });

            getAllInstitutions().then((res) => setInstitutions(res?.resultObject ?? []));
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const searchFunction = async (institutionId) => {
        try {
            setSearchValue(institutionId ?? '');
            setSearching(true);
            const res = await getCasesUploadSummaryForCPD(0, 50, false, institutionId);

            setRecords(res?.resultObject?.caseUploadHistoryResponseVm?.caseUploadHistoryVms || []);

            setPaginationData({
                start: 0,
                length: 50,
                total: res?.resultObject?.caseUploadHistoryResponseVm?.overallCount,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setSearching(false);
        }
    };

    const uploadFile = async () => {
        if (fileToUpload) {
            try {
                setUploadProcess('uploading');
                const formData = new FormData();
                formData.append('file', fileToUpload);
                await submitCases(formData);
                setUploadProcess('success');
                init();
            } catch (err) {
                enqueueSnackbar(err?.message || 'Failed to upload file', {
                    variant: 'error',
                });
                setUploadProcess('failed');
            }
        } else {
            enqueueSnackbar('Error: No File to upload', { variant: 'error' });
        }
    };

    const exportData = async () => {
        setExporting(true);
        try {
            const resp = await getCasesUploadSummaryForCPD(0, records?.length || 0, true);
            enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer pageTitle="Return Processing" processing={processing}>
            <Filters
                searchFields={[{ label: 'Institution', id: 'instId', options: institutions, field: 'institutionId', fieldDisplay: 'name', type: 'select' }]}
                useDefault={false}
                searchFunction={async (values) => {
                    // console.log(values);
                    await searchFunction(values.instId);
                }}
            />

            <Box>
                <div className="paper" style={{ height: 650, width: '100%', textAlign: 'center' }}>
                    {records?.length > 0 && (
                        <div className="export-btn mb-0">
                            <Button disabled={exporting} onClick={async () => await exportData('')} variant="outlined" color="info" startIcon={<FileDownloadOutlined />}>
                                {exporting ? <CircularProgress color="inherit" size={15} /> : 'Export'}
                            </Button>
                        </div>
                    )}

                    <small className="d-block text-left f-12 mb-05">
                        Validation status <b className="error">Failed</b> means that one or more cases within an upload, failed validation.
                    </small>
                    <DataGrid
                        className={'center-first-column'}
                        rows={records || []}
                        getRowId={(row) => row.caseUploadHistoryId}
                        columns={columns}
                        pageSize={paginationData?.length ?? records?.length ?? 0}
                        rowCount={paginationData?.total ?? records?.length ?? 0}
                        rowsPerPageOptions={[50]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        components={{
                            LoadingOverlay: LinearProgress,
                        }}
                        style={{ fontSize: '12px' }}
                        onPageChange={async (params) => {
                            await init(params * paginationData?.length, 50, searchValue);
                        }}
                        loading={processing || searching}
                    />
                </div>
            </Box>

            <Dialog
                open={openDialog}
                onClose={() => {
                    if (uploadProcess !== 'uploading') {
                        setOpenDialog(false);
                        setFileToUpload(null);
                    }
                }}
                TransitionComponent={Transition}
                keepMounted
                maxWidth="lg"
                disableEscapeKeyDown
            >
                <DialogContent>
                    {Boolean(uploadProcess) ? (
                        <div className="dropzone" style={{ cursor: 'initial' }}>
                            {Boolean(uploadProcess === 'uploading') ? (
                                <CircularProgress style={{ fontSize: '50px' }} />
                            ) : Boolean(uploadProcess === 'success') ? (
                                <CloudDone color="primary" />
                            ) : (
                                <Cancel style={{ fontSize: '50px', color: 'brown' }} />
                            )}
                            {Boolean(uploadProcess === 'success') && <h3 className="text-success mb-0">File Uploaded Successfully</h3>}
                            {Boolean(uploadProcess === 'failed') && <h3 className="text-error mb-0">File Uploaded Failed</h3>}
                            <p className="mt-05 mb-05">{fileToUpload?.name}</p>
                            <p className="m-0">{parseFloat(fileToUpload?.size / 1024).toFixed(2)} KB</p>
                        </div>
                    ) : (
                        <div {...getRootProps()}>
                            <input {...getInputProps()} multiple={false} accept="text/xml" />
                            <div className="dropzone">
                                {Boolean(fileToUpload) ? (
                                    <>
                                        <UploadFile color="primary" />
                                        <p className="mt-05 mb-05">{fileToUpload?.name}</p>
                                        <p className="m-0">{parseFloat(fileToUpload?.size / 1024).toFixed(2)} KB</p>
                                    </>
                                ) : (
                                    <>
                                        <CloudUpload color="primary" />
                                        <p>Drag and drop file here, or click to select files</p>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogActions className="p-0 pt-1">
                        <Button
                            onClick={() => {
                                if (uploadProcess !== 'uploading') {
                                    setOpenDialog(false);
                                    setFileToUpload(null);
                                }
                            }}
                            disabled={uploadProcess === 'uploading'}
                            variant="contained"
                            color="secondary"
                        >
                            {uploadProcess === 'success' || uploadProcess === 'failed' ? 'Close' : 'Cancel'}
                        </Button>
                        {Boolean(fileToUpload && uploadProcess !== 'success' && uploadProcess !== 'failed') && (
                            <Button onClick={uploadFile} disabled={uploadProcess === 'uploading'} variant="contained">
                                {uploadProcess === 'uploading' ? 'Uploading...' : 'Upload'}
                            </Button>
                        )}
                    </DialogActions>
                </DialogContent>
            </Dialog>

            <Drawer anchor="right" open={openDrawer}>
                <Grid container style={{ maxWidth: '50vw' }}>
                    <Grid item xs={12} container className="bg-primary p-2 pl-1" alignItems={'center'}>
                        <Grid item xs={11}>
                            Case Upload Details
                        </Grid>
                        <Grid item xs={1} justifyContent="flex-end" container>
                            <IconButton
                                color="inherit"
                                onClick={() => {
                                    setOpenDrawer(false);
                                    setCaseDetails(null);
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} flex={1}>
                        <List className="upload-details">
                            <ListItem>
                                <b>File Name: </b> {caseDetails?.originalFileName}
                            </ListItem>
                            <ListItem>
                                <b>Total Records: </b> {caseDetails?.totalRecords}
                            </ListItem>
                            <ListItem>
                                <b>Successful Records: </b> {caseDetails?.noOfSuccessRecord}
                            </ListItem>
                            <ListItem>
                                <b>Failed Records: </b> {caseDetails?.noOfFailedRecord}
                            </ListItem>
                        </List>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={value} onChange={handleChange} className={`${value === 0 ? 'error' : 'success'}-indicator`}>
                                    <Tab
                                        label="Failed Records"
                                        style={{
                                            color: value == 0 ? 'brown' : 'inherit',
                                        }}
                                    />
                                    <Tab
                                        label="Successful Records"
                                        style={{
                                            color: value == 1 ? PRIMARY_COLOR : 'inherit',
                                        }}
                                    />
                                </Tabs>
                            </Box>
                            <TabPanel value={value} index={0}>
                                <List className="upload-details">
                                    {Boolean(caseDetails?.caseUploadHistoryLogs?.length > 0) ? (
                                        caseDetails?.caseUploadHistoryLogs
                                            ?.filter((cs) => !Boolean(cs.isSuccessful))
                                            ?.map((cs, index) => (
                                                <ListItem key={index}>
                                                    <small>
                                                        <b>
                                                            {index + 1}. {cs.trackingReferenceNumber}:{' '}
                                                        </b>
                                                    </small>
                                                    <p className="m-0">{cs.message}</p>
                                                </ListItem>
                                            ))
                                    ) : (
                                        <p className="text-center">No Failed Records</p>
                                    )}
                                </List>
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <List className="upload-details">
                                    {Boolean(caseDetails?.caseUploadHistoryLogs?.length > 0) ? (
                                        caseDetails?.caseUploadHistoryLogs
                                            ?.filter((cs) => Boolean(cs.isSuccessful))
                                            ?.map((cs, index) => (
                                                <ListItem key={index}>
                                                    <small>
                                                        <b>
                                                            {index + 1}. {cs.trackingReferenceNumber}:{' '}
                                                        </b>
                                                    </small>
                                                    <p className="m-0">{cs.message}</p>
                                                </ListItem>
                                            ))
                                    ) : (
                                        <p className="text-center">No Successful Records</p>
                                    )}
                                </List>
                            </TabPanel>
                        </Box>
                    </Grid>
                </Grid>
            </Drawer>
        </PageContainer>
    );
};

export default ReturnSubmission;
